import type { ReactNode } from "react";

interface SectionHeadingProps {
  /** Section number e.g. "01" → rendered as "§ 01" in accent. Optional. */
  num?: string;
  /** Small mono label under the number (e.g. "manifiesto"). Optional. */
  numLabel?: string;
  /** Overline shown above the h2 (e.g. "Cómo te ayuda"). Optional. */
  overline?: string;
  /** Heading content — pass JSX so callers can style <em> spans. */
  heading: ReactNode;
  /** Extra bottom margin class (default mb-14). */
  className?: string;
  /** Heading size preset. Default `md` = 52px. */
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * Modern linear section header — overline/num stack + bold sans heading.
 * Replaces the old 80px grid sidebar layout.
 */
export function SectionHeading({
  num,
  overline,
  heading,
  className = "mb-14",
  size = "md",
}: SectionHeadingProps) {
  const sizeClass =
    size === "sm"
      ? "text-[26px] sm:text-[32px] md:text-[40px] leading-[1.1]"
      : size === "lg"
        ? "text-[40px] sm:text-[52px] md:text-[64px] leading-[1.02]"
        : size === "xl"
          ? "text-[48px] sm:text-[60px] md:text-[80px] leading-[1]"
          : "text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05]";

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4">
        {num && (
          <div className="text-[11px] font-mono tracking-[0.2em] uppercase text-[var(--color-accent)]">
            § {num}
          </div>
        )}
        {overline && (
          <div className="text-[11px] font-mono tracking-[0.2em] uppercase text-[var(--color-mute)]">
            {overline}
          </div>
        )}
      </div>
      <h2
        className={`font-sans font-bold tracking-[-0.02em] text-[var(--color-ink)] m-0 ${sizeClass} [&_em]:not-italic [&_em]:text-[var(--color-accent)] [&_em]:font-bold`}
      >
        {heading}
      </h2>
    </div>
  );
}
